import React, { useEffect, useRef, useState } from "react";

import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import {
  ArrowBack,
  CheckCircleOutline,
  CloudUploadOutlined,
  EditOutlined,
  PhoneOutlined,
  SchoolOutlined,
} from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { update } from "../../Slices/auth";
import api from "../../API/interceptors";

const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

const regions = [
  "dolnośląskie",
  "kujawsko-pomorskie",
  "lubelskie",
  "lubuskie",
  "łódzkie",
  "małopolskie",
  "mazowieckie",
  "opolskie",
  "podkarpackie",
  "podlaskie",
  "pomorskie",
  "śląskie",
  "świętokrzyskie",
  "warmińsko-mazurskie",
  "wielkopolskie",
  "zachodniopomorskie",
];

const educationOptions = [
  "Wyższe",
  "Średnie",
  "Zawodowe",
  "Podstawowe",
];

const educationTypeOptions = [
  "Budowlane",
  "Informatyczne",
  "Gastronomiczne",
  "Medyczne",
];

const validationSchema = yup.object({
  firstname: yup
    .string()
    .trim()
    .required("Imię jest wymagane")
    .min(3, "Imię musi mieć co najmniej 3 znaki"),

  lastname: yup
    .string()
    .trim()
    .required("Nazwisko jest wymagane")
    .min(3, "Nazwisko musi mieć co najmniej 3 znaki"),

  region: yup
    .string()
    .required("Województwo jest wymagane"),

  education: yup
    .string()
    .required("Wykształcenie jest wymagane"),

  typeOfEducation: yup
    .string()
    .required("Kierunek wykształcenia jest wymagany"),

  phone: yup
    .string()
    .required("Numer telefonu jest wymagany")
    .transform((value) => value?.replace(/\s/g, "") || "")
    .matches(/^[0-9]+$/, "Numer telefonu może zawierać tylko cyfry")
    .length(9, "Numer telefonu musi mieć dokładnie 9 cyfr"),
});

function Profile() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mobileWidth = useMediaQuery("(max-width:900px)");

  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [profileImage, setProfileImage] = useState(
    user?.profileImage || ""
  );

  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    defaultValues: {
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      region: user?.region || "",
      education: user?.education || "",
      typeOfEducation: user?.typeOfEducation || "",
      phone: user?.phone || "",
    },
  });


  useEffect(() => {
    setProfileImage(user?.profileImage || "");
  }, [user?.profileImage]);


  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handleSelectImage = (event) => {
    const selected = event.target.files?.[0];

    if (!selected) {
      return;
    }

    setSubmitError("");
    setSuccessMessage("");

    if (selected.size > 5 * 1024 * 1024) {
      setSubmitError("Zdjęcie nie może być większe niż 5 MB.");

      event.target.value = "";
      return;
    }


    if (!selected.type.startsWith("image/")) {
      setSubmitError("Wybierz prawidłowy plik graficzny.");

      event.target.value = "";
      return;
    }

 
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    const previewUrl = URL.createObjectURL(selected);

    previewUrlRef.current = previewUrl;

    setSelectedFile(selected);
    setImagePreview(previewUrl);
  };

  const handleRemoveSelectedImage = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    setSelectedFile(null);
    setImagePreview(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadProfileImage = async () => {
    if (!selectedFile) {
      return profileImage;
    }

    const formData = new FormData();

    formData.append("files", selectedFile);

    const response = await api({
      method: "post",
      url: `${UPLOADS_URL}/api/upload`,
      data: formData,
    });

    const uploadedImage = response?.data?.[0]?.url;

    if (!uploadedImage) {
      throw new Error("Serwer nie zwrócił adresu przesłanego zdjęcia.");
    }

    return uploadedImage;
  };

  const onSubmit = async (data) => {
    if (!user?.id) {
      setSubmitError("Nie znaleziono użytkownika.");
      return;
    }

    setIsSaving(true);
    setSubmitError("");
    setSuccessMessage("");

    try {
      const uploadedImage = await uploadProfileImage();


      await dispatch(
        update({
          id: user.id,
          token: user.jwt,
          payload: {
            ...data,
            ...(selectedFile
              ? {
                  profileImage: uploadedImage,
                }
              : {}),
          },
        })
      ).unwrap?.();

      if (selectedFile && uploadedImage) {
        setProfileImage(uploadedImage);
      }

      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }

      setSelectedFile(null);
      setImagePreview(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setSuccessMessage("Profil został pomyślnie zapisany.");


      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Błąd podczas zapisywania profilu:", error);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        "Nie udało się zapisać profilu. Spróbuj ponownie.";

      setSubmitError(
        Array.isArray(message)
          ? message.join(", ")
          : String(message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty || selectedFile) {
      const confirmed = window.confirm(
        "Masz niezapisane zmiany. Czy na pewno chcesz opuścić stronę?"
      );

      if (!confirmed) {
        return;
      }
    }

    navigate("/");
  };

  const currentAvatar = imagePreview || profileImage;

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        pt: mobileWidth ? "88px" : 4,
        pb: 6,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            mb: 4,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={handleCancel}
            sx={{
              mb: 2,
              textTransform: "none",
              color: "text.secondary",
            }}
          >
            Wróć
          </Button>

          <Typography
            component="h1"
            variant="h4"
            fontWeight={700}
            sx={{
              fontSize: {
                xs: "1.8rem",
                sm: "2.2rem",
              },
            }}
          >
            Profil użytkownika
          </Typography>

          <Typography
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            Zarządzaj swoimi danymi i zdjęciem profilowym.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 4 },
              py: { xs: 4, sm: 5 },
              background: (theme) =>
                `linear-gradient(
                  135deg,
                  ${theme.palette.primary.main}12 0%,
                  ${theme.palette.background.paper} 70%
                )`,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              alignItems="center"
            >
              <Box
                sx={{
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                <Avatar
                  src={currentAvatar}
                  alt={`${user?.firstname || ""} ${
                    user?.lastname || ""
                  }`}
                  sx={{
                    width: { xs: 120, sm: 140 },
                    height: { xs: 120, sm: 140 },
                    border: "4px solid",
                    borderColor: "background.paper",
                    boxShadow: 3,
                    fontSize: "2.5rem",
                  }}
                >
                  {user?.firstname?.charAt(0)}
                  {user?.lastname?.charAt(0)}
                </Avatar>

                <Button
                  component="label"
                  variant="contained"
                  size="small"
                  startIcon={<EditOutlined />}
                  disabled={isSaving}
                  sx={{
                    position: "absolute",
                    bottom: -8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    borderRadius: 5,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    px: 2,
                    boxShadow: 2,
                  }}
                >
                  Zmień zdjęcie

                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleSelectImage}
                  />
                </Button>
              </Box>

              <Box
                sx={{
                  textAlign: {
                    xs: "center",
                    sm: "left",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                >
                  {user?.firstname} {user?.lastname}
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Dane osobowe i informacje dotyczące
                  wykształcenia
                </Typography>

                {selectedFile && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent={{
                      xs: "center",
                      sm: "flex-start",
                    }}
                    sx={{ mt: 2 }}
                  >
                    <CloudUploadOutlined
                      fontSize="small"
                      color="primary"
                    />

                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight={600}
                    >
                      Nowe zdjęcie wybrane
                    </Typography>

                    <Button
                      size="small"
                      onClick={handleRemoveSelectedImage}
                      sx={{
                        textTransform: "none",
                        minWidth: 0,
                      }}
                    >
                      Usuń
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              p: {
                xs: 2,
                sm: 4,
              },
            }}
          >
            {successMessage && (
              <Alert
                severity="success"
                icon={<CheckCircleOutline />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                {successMessage}
              </Alert>
            )}

            {submitError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                {submitError}
              </Alert>
            )}

            <Box sx={{ mb: 4 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 2.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <EditOutlined fontSize="small" />
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                  >
                    Dane osobowe
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Uzupełnij podstawowe informacje o sobie.
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Imię"
                  autoComplete="given-name"
                  {...register("firstname")}
                  error={!!errors.firstname}
                  helperText={errors.firstname?.message}
                  disabled={isSaving}
                />

                <TextField
                  fullWidth
                  required
                  label="Nazwisko"
                  autoComplete="family-name"
                  {...register("lastname")}
                  error={!!errors.lastname}
                  helperText={errors.lastname?.message}
                  disabled={isSaving}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 2.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <PhoneOutlined fontSize="small" />
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                  >
                    Kontakt
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Numer telefonu kontaktowego.
                  </Typography>
                </Box>
              </Stack>

              <TextField
                fullWidth
                required
                label="Numer telefonu"
                placeholder="123456789"
                inputMode="numeric"
                autoComplete="tel"
                {...register("phone")}
                error={!!errors.phone}
                helperText={
                  errors.phone?.message ||
                  "Wpisz 9 cyfr bez spacji"
                }
                disabled={isSaving}
                InputProps={{
                  startAdornment: (
                    <PhoneOutlined
                      sx={{
                        mr: 1,
                        color: "text.secondary",
                      }}
                    />
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 2.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <SchoolOutlined fontSize="small" />
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                  >
                    Wykształcenie
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Informacje dotyczące Twojego wykształcenia.
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <Controller
                  name="region"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      required
                      error={!!errors.region}
                      disabled={isSaving}
                    >
                      <InputLabel id="profile-region-label">
                        Województwo
                      </InputLabel>

                      <Select
                        {...field}
                        labelId="profile-region-label"
                        id="profile-region"
                        label="Województwo"
                      >
                        {regions.map((item) => (
                          <MenuItem
                            key={item}
                            value={item}
                          >
                            {item}
                          </MenuItem>
                        ))}
                      </Select>

                      <FormHelperText>
                        {errors.region?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />

                <Controller
                  name="education"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      required
                      error={!!errors.education}
                      disabled={isSaving}
                    >
                      <InputLabel id="profile-education-label">
                        Wykształcenie
                      </InputLabel>

                      <Select
                        {...field}
                        labelId="profile-education-label"
                        id="profile-education"
                        label="Wykształcenie"
                      >
                        {educationOptions.map((item) => (
                          <MenuItem
                            key={item}
                            value={item}
                          >
                            {item}
                          </MenuItem>
                        ))}
                      </Select>

                      <FormHelperText>
                        {errors.education?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />

                <Controller
                  name="typeOfEducation"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      required
                      error={!!errors.typeOfEducation}
                      disabled={isSaving}
                    >
                      <InputLabel id="profile-education-type-label">
                        Kierunek wykształcenia
                      </InputLabel>

                      <Select
                        {...field}
                        labelId="profile-education-type-label"
                        id="profile-education-type"
                        label="Kierunek wykształcenia"
                      >
                        {educationTypeOptions.map((item) => (
                          <MenuItem
                            key={item}
                            value={item}
                          >
                            {item}
                          </MenuItem>
                        ))}
                      </Select>

                      <FormHelperText>
                        {errors.typeOfEducation?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                mb: 4,
                p: 2,
                borderRadius: 2,
                backgroundColor: "action.hover",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                <strong>Zdjęcie profilowe:</strong> JPG, PNG lub
                WebP, maksymalnie 5 MB.
              </Typography>
            </Box>

            <Stack
              direction={{
                xs: "column-reverse",
                sm: "row",
              }}
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{
                  minWidth: {
                    xs: "100%",
                    sm: 130,
                  },
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                Anuluj
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                sx={{
                  minWidth: {
                    xs: "100%",
                    sm: 160,
                  },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {isSaving ? "Zapisywanie..." : "Zapisz zmiany"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Profile;



// import React, { useState } from "react";
// import Button from "@mui/material/Button";
// import Stack from "@mui/material/Stack";
// import Box from "@mui/material/Box";
// import Typography from "@mui/material/Typography";
// import Container from "@mui/material/Container";
// import { useDispatch, useSelector } from "react-redux";
// import { Avatar, Paper, TextField, useMediaQuery } from "@mui/material";
// import MenuItem from "@mui/material/MenuItem";
// import Select from "@mui/material/Select";
// import InputLabel from "@mui/material/InputLabel";
// import FormControl from "@mui/material/FormControl";
// import { update } from "../../Slices/auth";
// import { useNavigate } from "react-router-dom";
// import IconButton from "@mui/material/IconButton";
// import PhotoCamera from "@mui/icons-material/PhotoCamera";
// import { styled } from "@mui/material/styles";
// import axios from "axios";
// import FormData from "form-data";

// import { useForm } from "react-hook-form";
// import FormHelperText from "@mui/material/FormHelperText";
// import { yupResolver } from "@hookform/resolvers/yup";
// import * as yup from "yup";
// import api from "../../API/interceptors";

// const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

// const regions = [
//   "dolnośląskie",
//   "kujawsko-pomorskie",
//   "lubelskie",
//   "lubuskie",
//   "łódzkie",
//   "małopolskie",
//   "mazowieckie",
//   "opolskie",
//   "podkarpackie",
//   "podlaskie",
//   "pomorskie",
//   "śląskie",
//   "świętokrzyskie",
//   "warmińsko-mazurskie",
//   "wielkopolskie",
//   "zachodniopomorskie",
// ];

// export default function Profile() {
//   const { user } = useSelector((state) => state.auth);
//   let navigate = useNavigate();
//   const dispatch = useDispatch();
//   const jwt = useSelector((state) => state.auth.accessToken);

//   const [profileImage, setProfileImage] = useState(user.profileImage);
//   const [firstname, setFirstname] = useState(user.firstname);
//   const [lastname, setLastname] = useState(user.lastname);
//   const [education, setEducation] = useState(user.education);
//   const [typeOfEducation, setTypeOfEducation] = useState(
//     user.typeOfEducation
//   );
//   const [region, setRegion] = useState(user.region);
//   const [phone, setPhone] = useState(user.phone);
//   const [file, setFile] = useState();
//   const [img, setImg] = useState();

//   const mobileWidth = useMediaQuery("(max-width:900px)");

//   const AdditionalFormSchema = yup.object().shape({
//     firstname: yup
//       .string()
//       .required("Imię jest wymagane")
//       .min(3, "Imię jest za krótkie"),
//     lastname: yup
//       .string()
//       .required("Nazwisko jest wymagane")
//       .min(3, "Nazwisko jest za krótkie"),
//     region: yup.string().required("Województwo jest wymagane"),
//     education: yup.string().required("Wykształcenie jest wymagane"),
//     typeOfEducation: yup
//       .string()
//       .required("Kierunek wykształcenia jest wymagany"),
//     phone: yup
//       .string()
//       .required("Numer jest wymagany")
//       .matches(/^[0-9]+$/, "Numer musi być liczbą")
//       .min(9, "Numer musi być 9 cyfrowy")
//       .max(9, "Numer musi być 9 cyfrowy"),
//   });

//   const {
//     register,
//     handleSubmit,
//     formState: { errors },
//   } = useForm({
//     resolver: yupResolver(AdditionalFormSchema),
//   });

//   const Input = styled("input")({
//     display: "none",
//   });

//   const handleChange = (e) => {
//     // axios.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
//     e.preventDefault();
//     setFile(e.target.files[0]);
//     setImg(URL.createObjectURL(e.target.files[0]));
//   };

//   const onSubmit = async (data) => {
//     const image = new FormData();
//     image.append("files", file);
//     // const upload_img = await axios({
//     const upload_img = await api({
//       method: "post",
//       url: `${UPLOADS_URL}/api/upload`,
//       data: image,
//       headers: { "Content-Type": "multipart/form-data" },
//     })
//       .then(function (response) {
//         setProfileImage(response.data[0].url);
//         dispatch(
//           update({
//             id: user.id,
//             token: user.jwt,
//             payload: { ...data, profileImage: response.data[0].url },
//           })
//         );
//       })
//       .catch(function (err) {
//         console.log("err", err);
//       });
//     dispatch(
//       update({
//         id: user.id,
//         token: user.jwt,
//         payload: data,
//       })
//     );
//     navigate("/");
//   };

//   return (
//     <main style={mobileWidth ? { marginTop: "88px" } : null}>
//       <Typography
//         component="h1"
//         variant="h4"
//         align="center"
//         color="text.primary"
//         sx={{ mt: 4 }}
//         gutterBottom
//       >
//         Profil użytkownika
//       </Typography>
//       <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
//         <Paper
//           variant="outlined"
//           sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}
//         >
//           <label htmlFor="icon-button-file">
//             <Input
//               accept="image/*"
//               id="icon-button-file"
//               type="file"
//               onChange={(e) => handleChange(e)}
//             />
//             <div style={{ width: "100%", textAlign: "center" }}>
//               <IconButton
//                 color="primary"
//                 aria-label="upload picture"
//                 component="span"
//               >
//                 <PhotoCamera
//                   sx={{
//                     position: "absolute",
//                     zIndex: 1,
//                     fontSize: "5.5rem",
//                     color: "#ffffffad",
//                   }}
//                 />
//                 <Avatar
//                   alt="User Avatar"
//                   src={img ? img : profileImage}
//                   sx={{ width: 130, height: 130, m: "auto" }}
//                 >
//                   <PhotoCamera
//                     sx={{
//                       position: "absolute",
//                       zIndex: 1,
//                       fontSize: "5.5rem",
//                       color: "#ffffffad",
//                     }}
//                   />
//                 </Avatar>
//               </IconButton>
//             </div>
//           </label>
//           <Typography
//             component="h2"
//             variant="h5"
//             align="center"
//             color="text.secondary"
//             paragraph
//             sx={{ mt: 4 }}
//           >
//             Twoje dane
//           </Typography>
//           <Box
//             component="form"
//             sx={{
//               "& .MuiTextField-root": { ml: 0, mb: 3, width: "100%" },
//             }}
//             noValidate
//             autoComplete="off"
//             onSubmit={handleSubmit(onSubmit)}
//           >
//             <div>
//               <TextField
//                 required
//                 id="outlined-required"
//                 label="Imię"
//                 defaultValue={firstname}
//                 onChange={(e) => setFirstname(e.target.value)}
//                 {...register("firstname")}
//                 helperText={errors.firstname && errors.firstname.message}
//                 error={errors.firstname !== undefined}
//               />
//               <TextField
//                 required
//                 id="outlined-required"
//                 label="Nazwisko"
//                 defaultValue={lastname}
//                 onChange={(e) => setLastname(e.target.value)}
//                 {...register("lastname")}
//                 helperText={errors.lastname && errors.lastname.message}
//                 error={errors.lastname !== undefined}
//               />
//               <FormControl
//                 fullWidth
//                 sx={{ mb: 3 }}
//                 error={errors.region !== undefined}
//               >
//                 <InputLabel id="demo-simple-select-label">
//                   Województwo
//                 </InputLabel>
//                 <Select
//                   labelId="demo-simple-select-label"
//                   id="demo-simple-select"
//                   defaultValue={region}
//                   onChange={(e) => setRegion(e.target.value)}
//                   label="Województwo"
//                   {...register("region")}
//                 >
//                   {regions.map((region) => (
//                     <MenuItem key={region} value={region}>
//                       {region}
//                     </MenuItem>
//                   ))}
//                 </Select>
//                 <FormHelperText>
//                   {errors.region && errors.region.message}
//                 </FormHelperText>
//               </FormControl>
//               <FormControl
//                 fullWidth
//                 sx={{ mb: 3 }}
//                 error={errors.education !== undefined}
//               >
//                 <InputLabel id="demo-simple-select-label">
//                   Wykształcenie
//                 </InputLabel>
//                 <Select
//                   name="education"
//                   labelId="demo-simple-select-label"
//                   id="demo-simple-select"
//                   defaultValue={education}
//                   label="Wykształcenie"
//                   onChange={(e) => setEducation(e.target.value)}
//                   {...register("education")}
//                 >
//                   <MenuItem value="Wyższe">Wyższe</MenuItem>
//                   <MenuItem value="Średnie">Średnie</MenuItem>
//                   <MenuItem value="Zawodowe">Zawodowe</MenuItem>
//                   <MenuItem value="Podstawowe">Podstawowe</MenuItem>
//                 </Select>
//                 <FormHelperText>
//                   {errors.education && errors.education.message}
//                 </FormHelperText>
//               </FormControl>
//               <FormControl
//                 fullWidth
//                 sx={{ mb: 3 }}
//                 error={errors.typeOfEducation !== undefined}
//               >
//                 <InputLabel id="demo-simple-select-label">
//                   Kierunek wykształcenia
//                 </InputLabel>
//                 <Select
//                   name="typeOfEducation"
//                   labelId="demo-simple-select-label"
//                   id="demo-simple-select"
//                   defaultValue={typeOfEducation}
//                   label="Kierunek wykształcenia"
//                   onChange={(e) => setTypeOfEducation(e.target.value)}
//                   {...register("typeOfEducation")}
//                 >
//                   <MenuItem value="Budowlane">Budowlane</MenuItem>
//                   <MenuItem value="Informatyczne">Informatyczne</MenuItem>
//                   <MenuItem value="Gastronomiczne">Gastronomiczne</MenuItem>
//                   <MenuItem value="Medyczne">Medyczne</MenuItem>
//                 </Select>
//                 <FormHelperText>
//                   {errors.typeOfEducation && errors.typeOfEducation.message}
//                 </FormHelperText>
//               </FormControl>
//               <TextField
//                 required
//                 id="outlined-required"
//                 label="Telefon"
//                 defaultValue={phone}
//                 onChange={(e) => setPhone(e.target.value)}
//                 {...register("phone")}
//                 helperText={errors.phone && errors.phone.message}
//                 error={errors.phone !== undefined}
//               />
//               <Stack
//                 direction="row"
//                 spacing={2}
//                 sx={{ justifyContent: "center" }}
//               >
//                 <Button type="submit" variant="outlined">
//                   Zapisz
//                 </Button>
//                 <Button variant="outlined" onClick={() => navigate("/")}>
//                   Odrzuć
//                 </Button>
//               </Stack>
//             </div>
//           </Box>
//         </Paper>
//       </Container>
//     </main>
//   );
// }
